/**
 * Client-side OTP login guards: verify attempt limits, resend cooldown, identifier helpers.
 * Defense-in-depth alongside server-side rate limiting.
 */

export const OTP_SECURITY = {
  MAX_VERIFY_ATTEMPTS: 5,
  VERIFY_LOCKOUT_MS: 15 * 60 * 1000,
  RESEND_COOLDOWN_MS: 60 * 1000,
  OTP_EXPIRY_SEC: 600,
} as const;

export type OtpChannel = 'sms' | 'email';

export interface OtpVerifyGuardState {
  isLocked: boolean;
  lockoutEndsAt: number | null;
  attemptsRemaining: number;
  failedAttempts: number;
}

interface StoredVerifyGuard {
  failedAttempts: number;
  lockoutEndsAt: number | null;
}

interface StoredResendGuard {
  lastSentAt: number;
}

const VERIFY_STORAGE_PREFIX = 'vaiu_otp_verify_';
const RESEND_STORAGE_PREFIX = 'vaiu_otp_resend_';

function storageKey(prefix: string, type: OtpChannel, identifier: string): string {
  const normalized = normalizeOtpIdentifier(type, identifier);
  return `${prefix}${type}_${hashIdentifier(normalized)}`;
}

function hashIdentifier(value: string): string {
  let hash = 5381;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 33) ^ value.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
}

function readJson<T>(key: string): T | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore quota / private mode errors
  }
}

function removeKey(key: string): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(key);
  } catch {
    // Ignore
  }
}

export function normalizeOtpIdentifier(type: OtpChannel, identifier: string): string {
  const trimmed = identifier.trim();
  return type === 'email' ? trimmed.toLowerCase() : trimmed;
}

export function isValidOtpIdentifier(type: OtpChannel, identifier: string): boolean {
  const normalized = normalizeOtpIdentifier(type, identifier);
  if (type === 'email') {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized);
  }
  return /^\+[1-9]\d{1,14}$/.test(normalized);
}

export function maskOtpIdentifier(type: OtpChannel, identifier: string): string {
  const normalized = normalizeOtpIdentifier(type, identifier);
  if (type === 'sms') {
    return normalized.replace(/(\+\d{1,3})\d+(\d{4})/, '$1****$2');
  }
  return normalized.replace(/(.{2}).+(@.+)/, '$1****$2');
}

export function formatLockoutDuration(remainingMs: number): string {
  const totalSec = Math.max(0, Math.ceil(remainingMs / 1000));
  const mins = Math.floor(totalSec / 60);
  const secs = totalSec % 60;
  if (mins > 0) {
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
  return `${secs}s`;
}

export function formatCountdown(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function toVerifyState(record: StoredVerifyGuard | null): OtpVerifyGuardState {
  const now = Date.now();
  const lockoutEndsAt = record?.lockoutEndsAt ?? null;
  const isLocked = lockoutEndsAt !== null && lockoutEndsAt > now;
  const failedAttempts = isLocked ? OTP_SECURITY.MAX_VERIFY_ATTEMPTS : (record?.failedAttempts ?? 0);
  const attemptsRemaining = Math.max(0, OTP_SECURITY.MAX_VERIFY_ATTEMPTS - failedAttempts);

  return {
    isLocked,
    lockoutEndsAt: isLocked ? lockoutEndsAt : null,
    attemptsRemaining,
    failedAttempts,
  };
}

export function getOtpVerifyGuardState(type: OtpChannel, identifier: string): OtpVerifyGuardState {
  const key = storageKey(VERIFY_STORAGE_PREFIX, type, identifier);
  const record = readJson<StoredVerifyGuard>(key);

  if (record?.lockoutEndsAt && record.lockoutEndsAt <= Date.now()) {
    removeKey(key);
    return toVerifyState(null);
  }

  return toVerifyState(record);
}

export function recordOtpVerifyFailure(type: OtpChannel, identifier: string): OtpVerifyGuardState {
  const key = storageKey(VERIFY_STORAGE_PREFIX, type, identifier);
  const existing = readJson<StoredVerifyGuard>(key);
  const failedAttempts = (existing?.failedAttempts ?? 0) + 1;
  const lockoutEndsAt =
    failedAttempts >= OTP_SECURITY.MAX_VERIFY_ATTEMPTS
      ? Date.now() + OTP_SECURITY.VERIFY_LOCKOUT_MS
      : null;

  writeJson(key, { failedAttempts, lockoutEndsAt } satisfies StoredVerifyGuard);
  return toVerifyState({ failedAttempts, lockoutEndsAt });
}

export function clearOtpVerifyGuard(type: OtpChannel, identifier: string): void {
  removeKey(storageKey(VERIFY_STORAGE_PREFIX, type, identifier));
}

export function getResendCooldownRemaining(type: OtpChannel, identifier: string): number {
  const key = storageKey(RESEND_STORAGE_PREFIX, type, identifier);
  const record = readJson<StoredResendGuard>(key);
  if (!record?.lastSentAt) return 0;
  const elapsed = Date.now() - record.lastSentAt;
  return Math.max(0, OTP_SECURITY.RESEND_COOLDOWN_MS - elapsed);
}

export function markOtpSent(type: OtpChannel, identifier: string): void {
  writeJson(storageKey(RESEND_STORAGE_PREFIX, type, identifier), {
    lastSentAt: Date.now(),
  } satisfies StoredResendGuard);
}

export function applyServerLockout(
  type: OtpChannel,
  identifier: string,
  lockedUntil: Date | string | number | null | undefined
): OtpVerifyGuardState {
  if (!lockedUntil) {
    return getOtpVerifyGuardState(type, identifier);
  }

  const lockoutEndsAt = new Date(lockedUntil).getTime();
  if (Number.isNaN(lockoutEndsAt) || lockoutEndsAt <= Date.now()) {
    return getOtpVerifyGuardState(type, identifier);
  }

  const key = storageKey(VERIFY_STORAGE_PREFIX, type, identifier);
  writeJson(key, {
    failedAttempts: OTP_SECURITY.MAX_VERIFY_ATTEMPTS,
    lockoutEndsAt,
  } satisfies StoredVerifyGuard);

  return toVerifyState({
    failedAttempts: OTP_SECURITY.MAX_VERIFY_ATTEMPTS,
    lockoutEndsAt,
  });
}
